import React, { useState, useEffect, useCallback } from "react";
// Neutral grey placeholder (no running-man, no dead remote service).
import { GREY_PLACEHOLDER as defaultImage } from "../../constants/imageFallback";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { userService, getPublicStaffProfile } from "../../services/api";
import { ROLES } from "../../config/roles";

import StaffHeroBanner from "../../components/common/StaffHeroBanner";
import ShareActionSheet from "../../components/common/ShareActionSheet";
import StaffPersonalInfo from "../../components/profile/StaffPersonalInfo";
import StaffStatistics from "../../components/profile/StaffStatistics";
import StaffCareerTimeline from "../../components/profile/StaffCareerTimeline";
import CoachingPersonalityCard from "../../components/profile/coaching-personality/CoachingPersonalityCard";
import { useUiLanguage } from "../../components/profile/coaching-personality/useUiLanguage";
import i18n from "../../i18n";
import { getStaticTranslation, getStaticTemplateMatch } from "../../translations/registerStaticTranslations";

const StaffProfile = () => {
  const { staffId } = useParams();
  const { currentRole } = useSelector((state) => state.auth); // Keep the Redux connection for future use
  // QAV5-502: window.alert() is a native browser dialog the DOM scanner
  // cannot reach — look the sentence up the same way the scanner does (exact
  // match first, template match second, e.g. for the "{count}" share count).
  const currentLanguage = useUiLanguage();
  const tr = (s) =>
    getStaticTranslation(i18n, s, currentLanguage) ||
    getStaticTemplateMatch(i18n, s, currentLanguage) ||
    s;
  const [activeTab, setActiveTab] = useState("personal-info");
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [isAvailableForHire, setIsAvailableForHire] = useState(false);
  const navigate = useNavigate();

  // Helper function to get profile picture from documents
  const getProfilePicture = (documents) => {
    if (!documents || !Array.isArray(documents)) return null;
    const profilePic = documents.find(doc => doc.doc_type === 'profile_photo' && doc.is_primary === true);
    return profilePic ? profilePic.url : documents.find(doc => doc.doc_type === 'profile_photo')?.url || null;
  };

  // Helper function to map API response to UI expected format
  const mapStaffData = useCallback((apiData) => {
    if (!apiData) return null;
    
    return {
      id: apiData.account_id,
      name: apiData.name || `${apiData.first_name || ''} ${apiData.last_name || ''}`.trim(),
      staff_type: apiData.staff_type || "Coach",
      role_type: apiData.role_type || "",
      works_for_club: apiData.works_for_club || false,
      additional_qualifications: apiData.additional_qualifications || "",
      address: apiData.address || "",
      city: apiData.city || "",
      country: apiData.country || "",
      document_url: getProfilePicture(apiData.documents),
      date_of_birth: apiData.date_of_birth,
      
      // Contact details for StaffPersonalInfo
      email: apiData.email || "",
      mobile: apiData.mobile || "",
      website: apiData.website_url || "",
      linkedin: apiData.linkedin_url || "",
      social: Array.isArray(apiData.social_urls) && apiData.social_urls.length > 0 ? apiData.social_urls[0] : "",
      
      // Work permissions
      passports: apiData.passports_held || [],
      workPermits: apiData.work_permits || [],
      contractStatus: apiData.contract_status || "",
      // Prefer the linked platform club's name (when staff.club is set on backend),
      // otherwise fall back to the free-text current_organization field.
      currentClub:
        apiData.club_details?.name || apiData.current_organization || "",
      club_details: apiData.club_details || null,
      current_organization: apiData.current_organization || "",
      contractExpiryDate: apiData.contract_expiry_date || "",
      willingnessToRelocate: apiData.willingness_to_relocate ? "Yes" : "No",
      preferredRegions: apiData.preferred_regions || [],
      
      // Technical skills
      videoAnalysis: apiData.technical_skills_video_analysis || [],
      gpsPerformance: apiData.technical_skills_gps_performance || [],
      software: apiData.technical_skills_software || [],
      specializations: apiData.specializations || [],
      
      // Career data
      trophies: (apiData.trophies || []).map((trophy, index) => ({
        id: index + 1,
        tournament_image: defaultImage,
        league_name: trophy.title,
        club_name: trophy.club,
        years: trophy.season,
      })),
      
      experience: (apiData.experience || []).map((exp, index) => ({
        id: index + 1,
        club_image: defaultImage,
        club_name: exp.club,
        position_name: exp.position,
        start_date: exp.start || "",
        end_date: exp.end || "Present",
        place: exp.country || "",
        type: exp.type === 'full_time' ? 'Full-time' : (exp.type === 'part_time' ? 'Part-time' : 'Contract'),
      })),
      
      licenses: (apiData.licenses || []).concat(apiData.gk_licenses || []).map(license => 
        typeof license === 'string' ? license : license.title
      ),
      
      languages: (apiData.languages || []).map(lang => 
        typeof lang === 'string' ? lang : lang.language || lang
      ),
      
      playingStyle: apiData.playing_styles || [],
      defensiveTactics: apiData.defensive_tactics || [],
      additionalNotes: apiData.coaching_notes || "",
      
      // For career timeline
      achievements: apiData.trophies || [],
      honors: apiData.honors_and_awards || [],
      contributions: apiData.notable_contributions || [],
      references: apiData.references || [],
      publications: apiData.publications || [],
      
      // Role specific data
      role_specific: apiData.role_specific || {},

      // Raw documents pool, passed through so StaffPersonalInfo can resolve
      // a license's own uploaded file (QAV5-405) the same way this file
      // already resolves the profile photo above (getProfilePicture).
      documents: apiData.documents || [],
    };
  }, []);

  useEffect(() => {
    const fetchStaffData = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log("Fetching staff profile for ID:", staffId);
        const response = await getPublicStaffProfile(staffId);
        
        if (response.success) {
          const mappedData = mapStaffData(response.data);
          console.log("Mapped staff data:", mappedData);
          setStaff(mappedData);
        } else {
          throw new Error(response.error || "Failed to fetch staff profile");
        }
      } catch (error) {
        console.error("Error fetching staff data:", error);
        setError("Failed to load staff data.");
      }

      setLoading(false);
    };

    if (staffId) {
      fetchStaffData();
    }
  }, [staffId, mapStaffData]);

  // Fetch current user profile to decide Share button visibility
  useEffect(() => {
    const fetchProfile = async () => {
      const res = await userService.getProfile();
      if (res.success) setCurrentUserProfile(res.data);
    };
    fetchProfile();
  }, []);

  const tabs = [
    { id: "personal-info", label: "Personal Info" },
    { id: "career-timeline", label: "Career Timeline" },
    { id: "statistics", label: "Statistics" },
  ];

  const handleShare = async (selectedUsers, shareUrl) => {
    try {
      console.log("Sharing with users:", selectedUsers);
      console.log("Share URL:", shareUrl);
      alert(tr(`Shared staff profile with ${selectedUsers.length} user(s)!`));
    } catch (error) {
      console.error("Error sharing:", error);
      alert(tr("Failed to share. Please try again."));
    }
  };

  const shareUrl = `${window.location.origin}/search/staff/${staffId}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-red-500">
        Error: {error}
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">
        Staff not found.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="w-full px-12 py-4 flex items-center justify-between border-b border-[#E5E6E8]">
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => navigate(-1)}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.8327 7.00033H1.16602M1.16602 7.00033L6.99935 12.8337M1.16602 7.00033L6.99935 1.16699"
              stroke="#1E1E1E"
              strokeWidth="1.875"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-xl font-bold text-[#222A35]">{staff.name}</span>
        </div>
        {(() => {
          const worksForClub = currentUserProfile?.user_details?.works_for_club;
          const hideForStaffNoClub =
            currentRole === ROLES.Staff && worksForClub === false;
          if (hideForStaffNoClub) return null;
          return (
            <button
              type="button"
              className="border border-[#E6E6E6] rounded-lg px-4 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors"
              onClick={() => setShowShareModal(true)}
              aria-label="Share staff profile"
            >
              <svg
                width="14"
                height="16"
                viewBox="0 0 14 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.72667 9.00634L9.28 11.6597M9.27333 4.33967L4.72667 6.99301M13 3.33301C13 4.43758 12.1046 5.33301 11 5.33301C9.89543 5.33301 9 4.43758 9 3.33301C9 2.22844 9.89543 1.33301 11 1.33301C12.1046 1.33301 13 2.22844 13 3.33301ZM5 7.99967C5 9.10424 4.10457 9.99967 3 9.99967C1.89543 9.99967 1 9.10424 1 7.99967C1 6.8951 1.89543 5.99967 3 5.99967C4.10457 5.99967 5 6.8951 5 7.99967ZM13 12.6663C13 13.7709 12.1046 14.6663 11 14.6663C9.89543 14.6663 9 13.7709 9 12.6663C9 11.5618 9.89543 10.6663 11 10.6663C12.1046 10.6663 13 11.5618 13 12.6663Z"
                  stroke="#2C2C2C"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-[#1E1E1E]">Share</span>
            </button>
          );
        })()}
      </div>

      {/* Hero Section Banner */}
      <div className="w-full px-10 pt-6">
        <StaffHeroBanner staff={staff} />
      </div>

      {/* Tabs */}
      <div className="border-b bg-white sticky top-0 z-20">
        <div className="w-full mx-auto px-12">
          <div className="flex justify-between items-center">
            <div className="flex gap-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`my-4 py-2 px-2 rounded-lg font-medium relative ${
                    activeTab === tab.id
                      ? "bg-[#F3F4F6]"
                      : "text-[#0B0B0B] hover:text-gray-900"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Right side - I'm Available for Hire button and toggle */}
         
         
            {/* <div className="flex items-center gap-4">
              <button className="flex items-center gap-3 text-[#292C33] text-sm font-medium">
                <svg
                  width="18"
                  height="17"
                  viewBox="0 0 18 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.9974 0.164551C4.3974 0.164551 0.664062 3.89788 0.664062 8.49788C0.664062 13.0979 4.3974 16.8312 8.9974 16.8312C13.5974 16.8312 17.3307 13.0979 17.3307 8.49788C17.3307 3.89788 13.5974 0.164551 8.9974 0.164551ZM9.83073 12.6646H8.16406V7.66455H9.83073V12.6646ZM9.83073 5.99788H8.16406V4.33122H9.83073V5.99788Z"
                    fill="#646B7E"
                  />
                </svg>
                I'm Available for Hire
              </button>

              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsAvailableForHire(!isAvailableForHire)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isAvailableForHire ? "bg-blue-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isAvailableForHire ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="w-full mx-auto px-12 py-8 bg-white">
        {/* Personal Info Tab Content */}
        {activeTab === "personal-info" && (
          <>
            <StaffPersonalInfo staff={staff} />
            {/* Coaching Personality, the outer card. `staffId` IS the account
                id this route resolves (users/urls.py:92), which is the key the
                card endpoint takes. The card draws itself only for a club whose
                own pitch this person sent AND whose switch is on; for everyone
                else the endpoint answers 404 and this renders nothing at all,
                so no wrapper, no spacing and no heading appears either. */}
            <CoachingPersonalityCard
              accountId={staffId}
              className="mt-6 max-w-[420px]"
            />
          </>
        )}

        {/* Career Timeline Tab Content */}
        {activeTab === "career-timeline" && (
          <StaffCareerTimeline staff={staff} />
        )}

        {/* Statistics Tab Content */}
        {activeTab === "statistics" && <StaffStatistics />}
      </div>

      <ShareActionSheet
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onShare={handleShare}
        title={`Share ${staff?.name || "Staff"} Profile`}
        shareUrl={shareUrl}
        shareType="staff_public_profile"
        shareData={{
          staff: {
            id: staff?.id,
            name: staff?.name || "Staff Name",
          },
        }}
      />
    </div>
  );
};

export default StaffProfile;
